<template>
  <div class="min-h-screen bg-jackfruit-50 text-ink-900">
    <NavBar />

    <main class="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <section class="overflow-hidden rounded-2xl border border-jackfruit-100 bg-white shadow-soft">
        <div class="grid gap-6 p-5 lg:grid-cols-[0.95fr_1.05fr] lg:p-8">
          <aside class="rounded-2xl bg-[#fff8df] p-5">
            <p class="text-xs font-black text-pulp-600">自由创作</p>
            <h1 class="mt-2 text-3xl font-black leading-tight text-ink-900 md:text-4xl">
              写个开头，让 AI 接着爆更
            </h1>
            <p class="mt-4 text-sm leading-7 text-ink-500">
              从 50-500 字的故事开端开始，菠萝蜜会帮你生成标题、封面和后续章节。满 5 章后，就可以申请上架公共书库，并有机会参与收益分成。
            </p>

            <div class="relative mt-6 rounded-2xl border border-jackfruit-100 bg-white p-4 pb-12">
              <div class="mb-3 flex items-center justify-between">
                <h2 class="text-sm font-black text-ink-900">灵感抽签</h2>
                <button
                  class="grid h-8 w-8 place-items-center rounded-lg text-pulp-600 transition hover:bg-jackfruit-50 hover:text-pulp-700"
                  title="换一组"
                  aria-label="换一组"
                  @click="shufflePrompt"
                >
                  <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M21 12a9 9 0 0 1-15.3 6.4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    <path d="M3 12A9 9 0 0 1 18.3 5.6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    <path d="M6 18H3v3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M18 6h3V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
              <div class="space-y-3 text-sm leading-6">
                <p><span class="font-black text-ink-900">题材：</span>{{ currentPrompt.genre }}</p>
                <p><span class="font-black text-ink-900">主角：</span>{{ currentPrompt.protagonist }}</p>
                <p><span class="font-black text-ink-900">冲突：</span>{{ currentPrompt.conflict }}</p>
                <p><span class="font-black text-ink-900">钩子：</span>{{ currentPrompt.hook }}</p>
              </div>
              <button
                class="absolute bottom-3 left-3 grid h-8 w-8 place-items-center rounded-lg text-ink-400 transition hover:bg-jackfruit-50 hover:text-ink-900"
                :title="copiedPrompt ? '已复制' : '复制灵感'"
                :aria-label="copiedPrompt ? '已复制' : '复制灵感'"
                @click="copyPrompt"
              >
                <svg v-if="!copiedPrompt" class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" stroke-width="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                <svg v-else class="h-4 w-4 text-leaf-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 6 9 17l-5-5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
          </aside>

          <section>
            <div v-if="step === 'opening'" class="space-y-4">
              <div>
                <label class="text-sm font-black text-ink-900" for="opening">第一屏内容</label>
                <textarea
                  id="opening"
                  v-model="opening"
                  rows="15"
                  maxlength="500"
                  class="mt-3 w-full resize-none rounded-2xl border border-jackfruit-200 bg-white px-4 py-4 text-sm leading-7 text-ink-800 outline-none transition focus:border-jackfruit-400"
                  placeholder="从一个人物、一个危机、一个秘密，或者一句狠话开始。"
                ></textarea>
                <div class="mt-2 flex items-center justify-between text-xs font-bold">
                  <span :class="openingLength < 50 ? 'text-red-500' : 'text-leaf-600'">{{ openingLength }}/500 字</span>
                  <span class="text-ink-400">50 字以上即可，像大纲也可以</span>
                </div>
              </div>

              <div class="flex flex-wrap gap-3">
                <select
                  v-model="category"
                  class="rounded-xl border border-jackfruit-200 bg-white px-4 py-3 text-sm font-black text-ink-700 outline-none focus:border-jackfruit-400"
                >
                  <option value="general">综合幻想</option>
                  <option value="male">男频爽文</option>
                  <option value="female">女频情感</option>
                  <option value="cyberpunk">赛博朋克</option>
                </select>
                <button
                  :disabled="openingLength < 50 || isGeneratingTitles"
                  class="rounded-xl bg-jackfruit-500 px-5 py-3 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600 disabled:cursor-not-allowed disabled:opacity-40"
                  @click="generateTitles"
                >
                  {{ isGeneratingTitles ? '生成中...' : '生成书名' }}
                </button>
              </div>
            </div>

            <div v-else-if="step === 'title'" class="space-y-5">
              <div>
                <h2 class="text-xl font-black text-ink-900">给故事起个上头的名字</h2>
                <p class="mt-1 text-sm text-ink-500">可以选 AI 候选，也可以自己写。</p>
              </div>

              <div class="flex flex-wrap gap-2">
                <button
                  v-for="option in titleOptions"
                  :key="option"
                  class="rounded-xl border px-4 py-2.5 text-sm font-black transition"
                  :class="title === option ? 'border-jackfruit-400 bg-jackfruit-100 text-ink-900' : 'border-jackfruit-100 bg-white text-ink-600 hover:bg-jackfruit-50'"
                  @click="title = option"
                >
                  {{ option }}
                </button>
              </div>

              <input
                v-model="title"
                maxlength="80"
                class="w-full rounded-xl border border-jackfruit-200 bg-white px-4 py-3 text-sm font-bold text-ink-900 outline-none focus:border-jackfruit-400"
                placeholder="也可以自己输入书名"
              >

              <div class="flex flex-wrap gap-3">
                <button class="rounded-xl border border-jackfruit-200 px-5 py-3 text-sm font-black text-ink-600 hover:bg-jackfruit-50" @click="step = 'opening'">
                  返回修改开头
                </button>
                <button
                  :disabled="!title.trim() || isGeneratingCovers"
                  class="rounded-xl bg-jackfruit-500 px-5 py-3 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600 disabled:cursor-not-allowed disabled:opacity-40"
                  @click="generateCovers"
                >
                  {{ isGeneratingCovers ? '生成中...' : '生成封面' }}
                </button>
              </div>
            </div>

            <div v-else class="space-y-5">
              <div>
                <h2 class="text-xl font-black text-ink-900">选一张封面</h2>
                <p class="mt-1 text-sm text-ink-500">封面之后会跟着作品一起进入书架。</p>
              </div>

              <div class="grid grid-cols-3 gap-3">
                <button
                  v-for="cover in coverOptions"
                  :key="cover"
                  class="overflow-hidden rounded-xl border-2 bg-jackfruit-50 transition"
                  :class="coverUrl === cover ? 'border-jackfruit-400 shadow-fruit' : 'border-transparent'"
                  @click="coverUrl = cover"
                >
                  <img :src="cover" :alt="title" class="aspect-[3/4] w-full object-cover">
                </button>
              </div>

              <div v-if="error" class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
                {{ error }}
              </div>

              <div class="flex flex-wrap gap-3">
                <button class="rounded-xl border border-jackfruit-200 px-5 py-3 text-sm font-black text-ink-600 hover:bg-jackfruit-50" @click="step = 'title'">
                  返回改书名
                </button>
                <button
                  :disabled="!coverUrl.trim() || isCreating"
                  class="rounded-xl bg-jackfruit-500 px-5 py-3 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600 disabled:cursor-not-allowed disabled:opacity-40"
                  @click="createBook"
                >
                  {{ isCreating ? 'AI 正在生成第一章...' : '生成第一章并开始续写' }}
                </button>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import NavBar from '@/components/NavBar.vue'
import { aiApi, bookApi } from '@/api'

type Step = 'opening' | 'title' | 'cover'

const router = useRouter()
const step = ref<Step>('opening')
const opening = ref('')
const title = ref('')
const category = ref<'male' | 'female' | 'general' | 'cyberpunk'>('general')
const titleOptions = ref<string[]>([])
const coverOptions = ref<string[]>([])
const coverUrl = ref('')
const error = ref('')
const copiedPrompt = ref(false)
const isGeneratingTitles = ref(false)
const isGeneratingCovers = ref(false)
const isCreating = ref(false)

const prompts = [
  { genre: '都市异能', protagonist: '刚被裁员的普通人', conflict: '手机里突然多出一个未来新闻 App', hook: '他看见明天的彩票号码，也看见了自己的死亡预告。' },
  { genre: '古言权谋', protagonist: '被退婚的将门嫡女', conflict: '新帝登基当晚，她收到一封来自十年后的密信', hook: '信上第一句是：别进宫，宫门后全是死人。' },
  { genre: '悬疑幻想', protagonist: '记忆会每天清零的侦探', conflict: '他接到一桩只有自己能破解的连环案', hook: '每具尸体手里，都攥着他昨天写下的名字。' },
  { genre: '修仙爽文', protagonist: '灵根被废的外门弟子', conflict: '宗门禁地里藏着一只会讲段子的上古器灵', hook: '器灵说：你别修仙了，修我这本欠债账吧。' },
  { genre: '甜宠娱乐圈', protagonist: '十八线替身演员', conflict: '她绑定了一个只会说真话的热搜系统', hook: '第一次直播，她把顶流影帝的暗恋对象说成了自己。' },
  { genre: '末日囤货', protagonist: '刚搬进老小区的单亲妈妈', conflict: '她发现小区地下车库连接着未来避难所', hook: '停电第一晚，所有邻居都听见了地下传来的广播。' },
  { genre: '赛博悬疑', protagonist: '专门修复记忆芯片的技师', conflict: '一位死者的芯片里存着他的童年录像', hook: '录像最后，他看见十岁的自己亲手关掉了警报。' },
  { genre: '校园轻喜', protagonist: '常年倒数第一的转学生', conflict: '他能听见试卷上每道题的吐槽', hook: '数学卷第一题说：别选 C，出题人今天心情不好。' },
  { genre: '民俗惊悚', protagonist: '回乡继承纸扎铺的年轻人', conflict: '祖父留下的账本记录着活人的寿命欠款', hook: '第一位客人进门时，影子却跪在门外不敢进来。' },
  { genre: '星际种田', protagonist: '被流放到荒星的植物学家', conflict: '她种下的第一颗番茄长出了会说话的舰载 AI', hook: 'AI 开口第一句话是：别浇水，我怕。' },
  { genre: '豪门反套路', protagonist: '准备跑路的协议妻子', conflict: '她突然能看到每个人头顶的隐藏剧本', hook: '丈夫头顶写着：今晚装冷漠，明天求复婚。' },
  { genre: '克系轻小说', protagonist: '经营深夜便利店的大学生', conflict: '店里每晚三点都会刷新一排不存在的商品', hook: '货架最上层那瓶汽水，标签写着他的出生日期。' },
  { genre: '历史脑洞', protagonist: '负责修文物的博物馆实习生', conflict: '她每修好一件文物，就会短暂进入原主人的一天', hook: '青铜镜修复完成后，她在镜中看见了还没死的自己。' },
  { genre: '热血竞技', protagonist: '退役边缘的职业选手', conflict: '他收到一套能预测对手微表情的训练系统', hook: '决胜局前，系统却提示：真正的对手在观众席。' },
  { genre: '萌宠奇幻', protagonist: '开宠物殡葬店的女孩', conflict: '她能听懂离世动物留下的最后一句话', hook: '一只老猫说：别相信我主人，他不是人。' },
  { genre: '玄学直播', protagonist: '靠假算命糊口的小主播', conflict: '某天她随口编的卦象全部应验', hook: '榜一大哥连麦问姻缘，她看见屏幕那头站着一个纸新娘。' },
  { genre: '废土公路', protagonist: '驾驶移动书店的少女', conflict: '她售出的每一本书都会改变一座城市的命运', hook: '今天有人买走了最后一本童话，城外的怪物开始唱歌。' },
  { genre: '职场爽文', protagonist: '被抢功劳的产品经理', conflict: '她能看到每个会议决策的未来成本', hook: '老板刚说完“问题不大”，她眼前弹出亏损三千万。' },
  { genre: '东方奇幻', protagonist: '给神明写悼词的小书吏', conflict: '他发现一位已故河神仍在偷偷批改自己的碑文', hook: '碑文最后一行多出四个字：我还没死。' }
]

const currentPromptIndex = ref(Math.floor(Math.random() * prompts.length))
const currentPrompt = computed(() => prompts[currentPromptIndex.value])
const openingLength = computed(() => opening.value.trim().length)
const genreLabel = computed(() => {
  return category.value === 'male' ? '男频爽文'
    : category.value === 'female' ? '女频小说'
      : category.value === 'cyberpunk' ? '赛博朋克小说'
      : '都市幻想'
})

const shufflePrompt = () => {
  let nextIndex = currentPromptIndex.value
  while (nextIndex === currentPromptIndex.value) {
    nextIndex = Math.floor(Math.random() * prompts.length)
  }
  currentPromptIndex.value = nextIndex
  copiedPrompt.value = false
}

const promptText = computed(() => [
  `题材：${currentPrompt.value.genre}`,
  `主角：${currentPrompt.value.protagonist}`,
  `冲突：${currentPrompt.value.conflict}`,
  `钩子：${currentPrompt.value.hook}`
].join('\n\n'))

const copyPrompt = async () => {
  await navigator.clipboard.writeText(promptText.value)
  copiedPrompt.value = true
}

const fallbackTitles = () => {
  const seed = opening.value.trim().slice(0, 12).replace(/[，。！？、\s]/g, '')
  return [
    `${seed || '开局'}之后我一路爆更`,
    `我的${genreLabel.value}不太对劲`,
    `从这一章开始逆转命运`
  ]
}

const generateTitles = async () => {
  if (openingLength.value < 50) return
  try {
    isGeneratingTitles.value = true
    error.value = ''
    titleOptions.value = fallbackTitles()

    const res = await aiApi.generateBookTitleOptions({
      originalTitle: '自由创作',
      description: opening.value.trim().slice(0, 500),
      genre: genreLabel.value,
      numOptions: 3
    })

    if (res.code === 200 && res.data?.titles?.length) {
      titleOptions.value = res.data.titles
    }
    title.value = titleOptions.value[0] || ''
    step.value = 'title'
  } catch (err) {
    titleOptions.value = fallbackTitles()
    title.value = titleOptions.value[0] || ''
    step.value = 'title'
  } finally {
    isGeneratingTitles.value = false
  }
}

const fallbackCover = () => {
  return '/placeholder-book.svg'
}

const generateCovers = async () => {
  if (!title.value.trim()) return
  try {
    isGeneratingCovers.value = true
    error.value = ''
    coverOptions.value = [fallbackCover()]
    coverUrl.value = coverOptions.value[0]

    const res = await aiApi.generateCoverOptions({
      novelTitle: title.value.trim(),
      novelDescription: opening.value.trim().slice(0, 500),
      genre: genreLabel.value,
      numOptions: 3
    })

    if (res.code === 200 && res.data?.options?.length) {
      const generated = res.data.options.map(option => option.imageUrl).filter(Boolean)
      coverOptions.value = generated.length ? generated : coverOptions.value
      coverUrl.value = coverOptions.value[0]
    }
    step.value = 'cover'
  } catch (err) {
    coverOptions.value = [fallbackCover()]
    coverUrl.value = coverOptions.value[0]
    step.value = 'cover'
  } finally {
    isGeneratingCovers.value = false
  }
}

const createBook = async () => {
  if (!title.value.trim() || !coverUrl.value.trim()) return

  try {
    isCreating.value = true
    error.value = ''
    const res = await bookApi.createOriginalBook({
      title: title.value.trim(),
      coverUrl: coverUrl.value.trim(),
      opening: opening.value.trim(),
      category: category.value
    })

    if (res.code === 200 && res.data?.book) {
      router.push(`/reading/${res.data.book.id}/1`)
    } else {
      throw new Error(res.message || '创建失败')
    }
  } catch (err: any) {
    error.value = err.message || '第一章生成失败，请稍后重试'
  } finally {
    isCreating.value = false
  }
}
</script>
